import { FEATURE_NAMES } from "@webscan/decide.js";
import { gallery } from "@webscan/retrieve.js";
import { decodeSetSymbols } from "@webscan/symbols.js";
import { loadOpenCv, orbReady, startWorker } from "@webscan/cvclient.js";
import type * as OrtNamespace from "onnxruntime-web";
import { loadRuntime, runtimeThreads } from "./runtime";
import { OcrReader } from "./ocr";
import { PoseDetector } from "./pose";
import { detectorExport } from "./detector-export";
import { clock, group, groupEnd, log, ms, mb as fmtMb, warn } from "../shared/debug";
import { Embedder } from "./embedder";
import { onSettingChange, settings } from "../shared/settings";
import { isIos } from "../shared/platform";
import { BackendRecovery } from "./backend-recovery";
import type { BootTaskId } from "../protocol/events";
let ort: typeof OrtNamespace;

/**
 * Required models load before recognition starts; camera permission and preview can
 * already be live, while optional evidence arrives independently in the background.
 *
 * When {@link boot} resolves, retrieval can identify a frame without another network
 * round trip. Optional evidence becomes available independently; ORB references are
 * fetched on demand as described by {@link ORB_NOTE}.
 */

/** Where the exporter's output is served from. Overridable for a CDN deployment. */
export const ASSET_BASE: string = import.meta.env.VITE_ASSET_BASE || "assets/";

/**
 * ORB reference descriptors are the one thing not fetched upfront, and cannot be.
 *
 * `descriptors.bin` is 977 MB. Verification reads a 206-byte slice of it per shortlisted
 * group, over HTTP `Range`. The index of those slices (`descriptor_spans.u32.bin`, 0.5 MB)
 * *is* loaded here, so the only thing left at scan time is a handful of tiny reads.
 */
export const ORB_NOTE =
  "ORB references are read as byte ranges out of descriptors.bin, not downloaded.";

/**
 * A token appended to every asset URL, derived from the contents of `meta.json`.
 *
 * The bundle is served `immutable`, which is right — 92 MB should not be revalidated on
 * every load. But `immutable` promises the bytes behind a URL never change, and re-running
 * the exporter changes them while the filenames stay put. A browser that has already seen
 * `detector.onnx` then keeps serving the old one forever, silently: the app boots, works,
 * and quietly runs a model you replaced.
 *
 * `meta.json` records every file's size, so a hash of it changes exactly when the bundle
 * does. It is fetched with `no-cache` so it is always the current one; everything else
 * stays immutable behind a URL that moves when the contents move.
 */
let assetVersion = "";

/**
 * Bump only when the Worker changes how an otherwise unchanged asset is delivered.
 *
 * Asset bodies are immutable at the edge, and so are their headers. This separate epoch
 * makes a response-policy fix visible immediately instead of waiting for an old response
 * with stale CORP/CORS headers to age out of every PoP cache.
 */
const ASSET_RESPONSE_EPOCH = "cors1";

/** The URL for a bundle file, cache-busted against the current bundle and response policy. */
export const assetUrl = (name: string): string =>
  `${ASSET_BASE}${name}${assetVersion}${assetVersion ? "&" : "?"}r=${ASSET_RESPONSE_EPOCH}`;

export interface BundleMeta {
  groups: number;
  printings: number;
  embed_dim: number;
  signature: boolean;
  calibration: boolean;
  set_symbols: number;
  descriptors: boolean;
  /** Whether the bundle ships the OCR recogniser (ocr_rec.onnx + charset). */
  ocr: boolean;
  bytes: Record<string, number>;
}

/**
 * One line of boot progress. Implemented by the scanner's own reporter, which turns
 * these calls into `boot:*` events for the view; nothing here touches the DOM.
 */
export interface BootTask {
  progress(loaded: number, total: number, cached?: boolean): void;
  done(note?: string): void;
  fail(message: string): void;
}
export interface BootAssetPlan {
  label: string;
  group: BootTaskId;
  total: number;
}
export interface BootReporter {
  (label: string, options?: { group?: BootTaskId; total?: number }): BootTask;
  plan?(entries: BootAssetPlan[]): void;
  resetFailures?(): void;
}

export interface Loaded {
  meta: BundleMeta;
  /** The DINOv2 embedder — the retrieval channel that survives foil and wear. */
  embedder: Embedder;
  /** The YOLO26n-pose corner regressor. */
  detector: PoseDetector;
  /** Which execution provider actually built. */
  backend: string;
  /** `[offset, count]` per group row into `descriptors.bin`; null when unavailable. */
  spans: Uint32Array | null;
  /** True once OpenCV-WASM is up in the shared worker and ORB can run. */
  orb: boolean;
  /**
   * The collector-number reader, or null while it is still arriving (it loads in the
   * background after boot — 21 MB that no scan strictly needs) or when the bundle
   * ships none. A read that lands while this is null simply stays symbol-only.
   */
  ocr: OcrReader | null;
  /** Non-fatal things the user should know about. */
  warnings: string[];
}

interface Transfer {
  buffer: Uint8Array | null;
  chunks: Uint8Array[];
  loaded: number;
  total: number;
  complete: boolean;
}
/** Kept across retries, then released once required models have been constructed. */
const transfers = new Map<string, Transfer>();
const invalidTransfers = new Set<string>();
const detectors = new Map<string, PoseDetector>();
const embedders = new Map<string, Embedder>();
const backendRecovery = new BackendRecovery(
  (() => {
    try {
      return sessionStorage;
    } catch {
      return null;
    }
  })(),
);
let bootBackend: string | null = null;
onSettingChange((key) => {
  if (key === "backend") backendRecovery.clear();
});

/** Called only after all boot jobs have settled, before restarting on a fresh page. */
export function prepareBackendRecovery(error: unknown): boolean {
  return backendRecovery.arm(
    settings.backend,
    bootBackend,
    error instanceof Error ? error.message : String(error),
    typeof WebAssembly !== "undefined",
  );
}

/** A body that arrived but could not be parsed or built must not poison every retry. */
function invalidateCompleteTransfer(url: string): void {
  if (!transfers.get(url)?.complete) return;
  transfers.delete(url);
  invalidTransfers.add(url);
}

/** One streaming fetch, resuming interrupted bodies when the server supports Range. */
async function fetchProgressOnce(url: string, task: BootTask): Promise<Uint8Array> {
  const elapsed = clock();
  let transfer = transfers.get(url);
  if (transfer?.complete && transfer.buffer) {
    task.progress(transfer.loaded, transfer.total, true);
    return transfer.buffer;
  }
  const offset = transfer?.loaded ?? 0;
  const response = await fetch(url, {
    ...(offset ? { headers: { Range: `bytes=${offset}-` } } : {}),
    ...(invalidTransfers.has(url) ? { cache: "reload" as const } : {}),
  });
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  invalidTransfers.delete(url);
  const range = response.headers.get("content-range")?.match(/^bytes (\d+)-\d+\/(\d+)$/);
  const resumed = response.status === 206 && range && Number(range[1]) === offset;
  if (response.status === 206 && !resumed) throw new Error("Invalid download range response");
  const total = resumed ? Number(range[2]) : Number(response.headers.get("content-length") || 0);
  if (!resumed || !transfer) {
    transfer = {
      buffer: total > 0 ? new Uint8Array(total) : null,
      chunks: [],
      loaded: 0,
      total,
      complete: false,
    };
    transfers.set(url, transfer);
  }
  // Worth logging: a bundle served without content-length cannot show real progress, and
  // one served from cache is the difference between a 4-second boot and a 90-second one.
  const cached = response.headers.get("age") ?? null;
  if (!response.body) {
    const bytes = new Uint8Array(await response.arrayBuffer());
    transfer.buffer = bytes;
    transfer.loaded = bytes.byteLength;
    transfer.total = bytes.byteLength;
    transfer.complete = true;
    task.progress(bytes.byteLength, bytes.byteLength);
    log("boot", `${url} — ${fmtMb(bytes.byteLength)} in ${ms(elapsed())} (unstreamed)`);
    return bytes;
  }

  const reader = response.body.getReader();
  // The models have Content-Length. Fill the final array as chunks arrive instead of
  // retaining 95 MB of chunks and then allocating/copying another 95 MB at EOF — that
  // transient double peak was enough to make a cold iPhone boot needlessly fragile.
  const bytes = transfer.buffer;
  const chunks = transfer.chunks;
  let loaded = transfer.loaded;
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    if (bytes) {
      if (loaded + value.byteLength > bytes.byteLength) {
        throw new Error(`body exceeded Content-Length (${loaded + value.byteLength} > ${total})`);
      }
      bytes.set(value, loaded);
    } else {
      chunks.push(value);
    }
    loaded += value.byteLength;
    transfer.loaded = loaded;
    task.progress(loaded, total);
  }
  if (bytes && loaded !== bytes.byteLength) {
    throw new Error(`body ended before Content-Length (${loaded} < ${total})`);
  }
  const result = bytes ?? new Uint8Array(loaded);
  if (!bytes) {
    let at = 0;
    for (const chunk of chunks) {
      result.set(chunk, at);
      at += chunk.byteLength;
    }
  }
  transfer.buffer = result;
  transfer.chunks = [];
  transfer.total = loaded;
  transfer.complete = true;
  const timing = performance.getEntriesByName(new URL(url, document.baseURI).href).at(-1) as
    PerformanceResourceTiming | undefined;
  task.progress(
    loaded,
    loaded,
    !!timing && timing.transferSize === 0 && timing.decodedBodySize > 0,
  );
  const took = elapsed();
  log(
    "boot",
    `${url} — ${fmtMb(loaded)} in ${ms(took)}` +
      ` (${((loaded / 1e6 / took) * 1000).toFixed(1)} MB/s${cached ? `, cache age ${cached}s` : ""})`,
  );
  return result;
}

/** Fetch with progress and one bounded retry for an interrupted transfer. */
async function fetchProgress(url: string, task: BootTask): Promise<Uint8Array> {
  try {
    return await fetchProgressOnce(url, task);
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    const interrupted = error instanceof TypeError || /network|fetch|load failed/i.test(message);
    if (!interrupted) throw error;
    warn("boot", `${url} interrupted (${message}) — retrying once`);
    await new Promise<void>((resolve) => window.setTimeout(resolve, 250));
    return fetchProgressOnce(url, task);
  }
}

const nf = new Intl.NumberFormat();
const mb = (bytes: number) => `${(bytes / 1e6).toFixed(bytes < 1e7 ? 1 : 0)} MB`;

/**
 * Pick one execution provider before either model is fetched or constructed.
 *
 * iPhone/iPad use the ordinary CPU runtime: WebGPU initialization exhausted memory and
 * the larger WebGL model/pool did not recover scanning on the reported device. This
 * path uses the matched FP16 embedder and avoids both GPU model allocation and JSEP.
 * Desktop still prefers WebGPU; explicit selection remains available for measurements.
 */
async function chooseBackend(): Promise<string[]> {
  const appleMobile = isIos();
  // Do not probe with getContext(): on iOS even a discarded probe can keep a second GL
  // context alive until GC. ORT will perform the one real context creation at build time.
  const hasWebGl2 = (): boolean => typeof WebGL2RenderingContext !== "undefined";
  const webGpuAdapter = async (): Promise<unknown | null> => {
    const gpu = (navigator as { gpu?: { requestAdapter(): Promise<unknown> } }).gpu;
    if (!gpu) return null;
    return await gpu.requestAdapter();
  };

  if (backendRecovery.preferred(settings.backend)) {
    log("model", "GPU startup failed previously — recovering with the CPU runtime in this tab");
    return ["wasm"];
  }

  if (settings.backend === "wasm") {
    log("model", "backend=CPU — forcing every session onto the CPU");
    return ["wasm"];
  }
  if (settings.backend === "webgl") {
    if (!hasWebGl2()) throw new Error("WebGL2 is required for the selected model backend");
    log(
      "model",
      appleMobile
        ? "iPhone/iPad detected — WebGL2 was explicitly selected"
        : "backend=WebGL2 — forcing both model sessions onto WebGL2",
    );
    return ["webgl"];
  }

  if (settings.backend === "webgpu") {
    const adapter = await webGpuAdapter();
    if (!adapter) throw new Error("WebGPU was selected but no adapter is available");
    log("model", "backend=WebGPU — adapter acquired", adapter);
    return ["webgpu"];
  }

  if (appleMobile) {
    log(
      "model",
      "iPhone/iPad automatic backend — using the standard CPU runtime with one WASM thread",
    );
    return ["wasm"];
  }

  try {
    const adapter = (await webGpuAdapter()) as { info?: unknown } | null;
    if (adapter) {
      log(
        "model",
        `${appleMobile ? "iPhone/iPad detected — " : ""}WebGPU adapter acquired`,
        adapter.info ?? adapter,
      );
      return ["webgpu"];
    }
  } catch (error) {
    log("model", `requestAdapter() threw (${(error as Error).message})`);
  }

  if (hasWebGl2()) {
    log("model", "WebGPU unavailable — using the WebGL2 model exports");
    return ["webgl"];
  }
  log("model", "no GPU execution provider is available — using CPU");
  return ["wasm"];
}

/** Run a job now; the stand-in for boot's concurrency gate outside boot. */
type Limiter = <T>(work: () => Promise<T>) => Promise<T>;
const direct: Limiter = (work) => work();

/**
 * The ORB half: OpenCV-WASM in the shared worker, and the index of reference spans.
 *
 * Both are needed — OpenCV to extract the query's features, the spans to find the
 * references to compare them against — so they load and fail together, and the caller
 * gets one `orb` that means "verification can actually run".
 */
async function loadOrbStage(
  meta: BundleMeta,
  report: BootReporter,
  limit: Limiter,
): Promise<{ orb: boolean; spans: Uint32Array | null; warnings: string[] }> {
  const warnings: string[] = [];
  const orbTask = report("OpenCV (ORB)");
  const opencvReady = (async () => {
    const worker = startWorker();
    log("boot", `detection worker ${worker ? "started" : "unavailable"}; fetching OpenCV-WASM…`);
    const opencv = clock();
    const state = await loadOpenCv();
    log("boot", `OpenCV load returned in ${ms(opencv())} — backend "${state.backend}"`, state);
    return state;
  })().then(
    (value) => ({ value, error: null }),
    (error: unknown) => ({ value: null, error }),
  );

  const spans = meta.descriptors
    ? await limit(async () => {
        const task = report("descriptor index");
        try {
          const bytes = await fetchProgress(assetUrl(`descriptor_spans.u32.bin`), task);
          const value = new Uint32Array(bytes.buffer, bytes.byteOffset, bytes.byteLength / 4);
          task.done(`${nf.format(value.length / 2)} groups`);
          return value;
        } catch (error) {
          task.fail((error as Error).message);
          warnings.push("No descriptor index; scans run on retrieval evidence alone.");
          return null;
        }
      })
    : null;

  let opencv = false;
  try {
    const result = await opencvReady;
    if (!result.value) throw result.error;
    const state = result.value;
    opencv = orbReady();
    if (opencv) orbTask.done("verification on");
    else throw new Error(state.error || "worker unavailable");
  } catch (error) {
    orbTask.fail((error as Error).message);
    warnings.push(
      `No geometric verification (${(error as Error).message}); ` +
        `cards will read as ambiguous far more often.`,
    );
  }

  // A bundle exported without descriptors has neither failed nor warned above, so say it
  // here rather than let verification be quietly off.
  if (opencv && !spans) {
    warnings.push("This bundle ships no ORB references; scans run on retrieval alone.");
  }
  return { orb: opencv && !!spans, spans, warnings };
}

/**
 * The set-symbol table — the only reprint disambiguator that works on a frame printing no
 * collector number at all, which is every 1993, 1997 and full-art card.
 *
 * Version 2 carries edge templates; the version-1 dHash table measured useless and is
 * refused by the decoder, leaving the channel off exactly as if the file were absent.
 * Returns what the user should be told, if anything.
 */
async function loadSymbolTable(report: BootReporter): Promise<string[]> {
  const task = report("set symbols");
  try {
    const bytes = await fetchProgress(assetUrl("set_symbols.json"), task);
    const table = decodeSetSymbols(JSON.parse(new TextDecoder().decode(bytes)));
    gallery.setSymbols = table;
    if (table) {
      task.done(`${nf.format(table.size)} symbol buckets`);
      return [];
    }
    task.fail("not a version-2 template table");
    return [
      "The bundle's set-symbol table predates the edge templates; " +
        "reprints will stay ambiguous more often until it is re-exported.",
    ];
  } catch (error) {
    task.fail((error as Error).message);
    gallery.setSymbols = null;
    return ["No set-symbol table; reprints will stay ambiguous more often."];
  }
}

/** The collector-number reader, onto `loaded.ocr` when it is up. */
async function loadOcrReader(loaded: Loaded, report: BootReporter): Promise<void> {
  const task = report("ocr_rec.onnx");
  try {
    const [model, charsetResponse] = await Promise.all([
      fetchProgress(assetUrl("ocr_rec.onnx"), task),
      fetch(assetUrl("ocr_charset.txt")),
    ]);
    if (!charsetResponse.ok) throw new Error(`charset: HTTP ${charsetResponse.status}`);
    loaded.ocr = await OcrReader.create(
      await loadRuntime("wasm"),
      model,
      await charsetResponse.text(),
    );
    task.done("collector numbers on");
    log("boot", "OCR reader up — printings that differ only by collector number resolve now");
  } catch (error) {
    task.fail((error as Error).message);
    loaded.warnings.push(
      "Collector-number recognition is unavailable; some printings need review.",
    );
    warn("boot", "OCR unavailable — same-set reprints stay ambiguous", error);
  }
}

/** Boot's reporter, kept so a stage switched on later can report its own load. */
let lastReport: BootReporter = () => ({ progress() {}, done() {}, fail() {} });

/** In-flight on-demand loads, so a double tap on a switch does not fetch twice. */
const pending = new Map<string, Promise<void>>();

/**
 * Load what a stage needs, if it is not already in memory.
 *
 * Turning a stage off is free — the funnel simply reads the setting — but turning one
 * back on may need the asset that boot deliberately did not fetch. This is that fetch,
 * and it reports into the same boot list, so a stage arriving late looks exactly like a
 * stage arriving early. Resolves either way: a failure has already been reported on the
 * task, and the funnel keeps checking `loaded` before it uses anything.
 */
export function ensureStage(loaded: Loaded, stage: "orb" | "ocr" | "setSymbols"): Promise<void> {
  const running = pending.get(stage);
  if (running) return running;
  const job = (async () => {
    if (stage === "orb") {
      if (loaded.orb) return;
      const result = await loadOrbStage(loaded.meta, lastReport, direct);
      loaded.orb = result.orb;
      loaded.spans = result.spans;
      loaded.warnings.push(...result.warnings);
    } else if (stage === "ocr") {
      if (loaded.ocr || !loaded.meta.ocr) return;
      await loadOcrReader(loaded, lastReport);
    } else {
      if (gallery.setSymbols || !loaded.meta.set_symbols) return;
      loaded.warnings.push(...(await loadSymbolTable(lastReport)));
    }
  })().finally(() => {
    pending.delete(stage);
    // Extras are skipped on failure, so retaining their full preallocated buffers would
    // permanently spend memory for evidence the scanner cannot use. Keep only active
    // transfers belonging to other stages; required boot buffers have already cleared.
    const file = {
      orb: "descriptor_spans.u32.bin",
      ocr: "ocr_rec.onnx",
      setSymbols: "set_symbols.json",
    }[stage];
    transfers.delete(assetUrl(file));
  });
  pending.set(stage, job);
  return job;
}

/**
 * Load the index columns, the catalogue, the policy and both models.
 *
 * Fatal: the meta file, the hash columns, the catalogue and the two models — without any
 * of them there is no scanner. Non-fatal and reported: the calibration, the set-symbol
 * table and the whole ORB half. The page degrades rather than refusing to start, but it
 * says what it lost. What the user has switched off in settings is not loaded at all.
 */
export async function boot(report: BootReporter): Promise<Loaded> {
  bootBackend = null;
  const warnings: string[] = [];
  const bootClock = clock();
  log("boot", `asset base ${ASSET_BASE}`);
  log(
    "boot",
    `crossOriginIsolated=${self.crossOriginIsolated} hardwareConcurrency=${navigator.hardwareConcurrency} ` +
      `dpr=${window.devicePixelRatio} ua=${navigator.userAgent}`,
  );

  const metaTask = report("index metadata", { group: "index" });
  let meta: BundleMeta;
  try {
    // `no-cache` revalidates rather than trusting the cache: this file is the thing
    // that tells everything else whether it is stale.
    const response = await fetch(`${ASSET_BASE}meta.json`, { cache: "no-cache" });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const text = await response.text();
    const metaBytes = new TextEncoder().encode(text).byteLength;
    metaTask.progress(metaBytes, metaBytes);
    // FNV-1a over the manifest — cheap, and it only has to change when the bundle does.
    let hash = 0x811c9dc5;
    for (let i = 0; i < text.length; i++) {
      hash = Math.imul(hash ^ text.charCodeAt(i), 0x01000193) >>> 0;
    }
    assetVersion = `?v=${hash.toString(36)}`;
    meta = JSON.parse(text) as BundleMeta;
    metaTask.done(`${nf.format(meta.groups)} artworks`);
    group(
      "boot",
      `bundle: ${nf.format(meta.groups)} artworks, ${nf.format(meta.printings)} printings`,
    );
    log("boot", `bundle version ${assetVersion}`);
    log("boot", "meta.json", meta);
    groupEnd();
  } catch (error) {
    metaTask.fail((error as Error).message);
    throw new Error(
      `Could not read ${ASSET_BASE}meta.json (${(error as Error).message}). Build the bundle first:\n` +
        `  python tools/export_web_assets.py --index index-v2 --out webscan/assets`,
    );
  }

  gallery.groups = meta.groups;
  gallery.embedDim = meta.embed_dim;
  gallery.hasSignature = !!meta.signature;
  gallery.hasEmbeddings = meta.embed_dim > 0;

  /** One index column: the file, and where its typed view belongs on the gallery. */
  interface Column {
    file: string;
    apply(bytes: Uint8Array): void;
  }
  const view = (bytes: Uint8Array) =>
    [bytes.buffer as ArrayBuffer, bytes.byteOffset] as [ArrayBuffer, number];

  // Typed views onto one buffer each, exactly as the retrieval kernels index them. The
  // stride is implicit in the element type, so the element count is derived rather than
  // configured — a column whose width changed would fail loudly here.
  const columns: Column[] = [
    {
      file: "hashes.u32.bin",
      apply: (b) => void (gallery.hashes = new Uint32Array(...view(b), b.byteLength / 4)),
    },
    ...(meta.signature
      ? [
          {
            file: "phash256.u32.bin",
            apply: (b: Uint8Array) =>
              void (gallery.phash256 = new Uint32Array(...view(b), b.byteLength / 4)),
          },
          {
            file: "colour.u8.bin",
            apply: (b: Uint8Array) =>
              void (gallery.colour = new Uint8Array(...view(b), b.byteLength)),
          },
          {
            file: "tiles.u32.bin",
            apply: (b: Uint8Array) =>
              void (gallery.tiles = new Uint32Array(...view(b), b.byteLength / 4)),
          },
        ]
      : []),
    ...(gallery.hasEmbeddings
      ? [
          {
            file: "embeddings.i8.bin",
            apply: (b: Uint8Array) =>
              void (gallery.embeddings = new Int8Array(...view(b), b.byteLength)),
          },
          {
            file: "embed_scales.f32.bin",
            apply: (b: Uint8Array) =>
              void (gallery.embedScales = new Float32Array(...view(b), b.byteLength / 4)),
          },
        ]
      : []),
  ];

  // ---- everything else, concurrently -----------------------------------------

  // Sequential fetching was costing more than every other part of boot combined: each
  // file waited for the one before it, so 86 MB arrived at one file's worth of throughput
  // at a time. On a high-latency link a single stream is bandwidth-delay limited well
  // below the line rate, and several in flight genuinely multiply it. The cap keeps peak
  // memory bounded — these buffers are all live at once while they land.
  const CONCURRENCY = 4;
  let active = 0;
  const queue: Array<() => void> = [];
  const limited = async <T>(work: () => Promise<T>): Promise<T> => {
    if (active >= CONCURRENCY) await new Promise<void>((resolve) => queue.push(resolve));
    active++;
    try {
      return await work();
    } finally {
      active--;
      queue.shift()?.();
    }
  };

  /**
   * Session construction, one at a time — downloads stay concurrent.
   *
   * Building two WebGPU sessions at once races inside onnxruntime's device and pipeline
   * setup, and the second one comes back with an undefined compute pipeline:
   * `Cannot read properties of undefined (reading 'getBindGroupLayout')`. Sequentially,
   * both build fine. Only the *builds* are serialised, so the detector still compiles its
   * shaders while the embedder is still arriving — which was the point.
   */
  let buildChain: Promise<unknown> = Promise.resolve();
  const buildSerial = <T>(work: () => Promise<T>): Promise<T> => {
    const next = buildChain.then(work, work);
    // A failed build must not poison the queue for the one behind it.
    buildChain = next.catch(() => undefined);
    return next;
  };

  const providers = await chooseBackend();
  bootBackend = providers[0];
  ort = await loadRuntime(providers[0]);
  const wasmThreads = runtimeThreads();
  const webgl = providers[0] === "webgl";
  const selectedDetector = detectorExport(
    settings.detector,
    providers[0],
    new URLSearchParams(location.search),
    meta.bytes,
  );
  const detectorFile = selectedDetector.file;
  if (selectedDetector.note) log("model", selectedDetector.note);
  const embedderFile = webgl ? "embedder-webgl.onnx" : "embedder.onnx";
  const columnLabel = (file: string) => file.replace(/\.\w+\.bin$/, "");
  report.plan?.([
    { label: "index metadata", group: "index", total: 0 },
    { label: detectorFile, group: "detector", total: meta.bytes[detectorFile] ?? 0 },
    { label: embedderFile, group: "recogniser", total: meta.bytes[embedderFile] ?? 0 },
    { label: "catalogue", group: "index", total: meta.bytes["catalogue.json"] ?? 0 },
    ...columns.map((column): BootAssetPlan => ({
      label: columnLabel(column.file),
      group: "index",
      total: meta.bytes[column.file] ?? 0,
    })),
    ...(meta.calibration
      ? [
          {
            label: "decision policy",
            group: "extras" as const,
            total: meta.bytes["calibration.json"] ?? 0,
          },
        ]
      : []),
    ...(meta.set_symbols && settings.setSymbols
      ? [
          {
            label: "set symbols",
            group: "extras" as const,
            total: meta.bytes["set_symbols.json"] ?? 0,
          },
        ]
      : []),
    ...(settings.orb
      ? [
          { label: "OpenCV (ORB)", group: "extras" as const, total: 0 },
          ...(meta.descriptors
            ? [
                {
                  label: "descriptor index",
                  group: "extras" as const,
                  total: meta.bytes["descriptor_spans.u32.bin"] ?? 0,
                },
              ]
            : []),
        ]
      : []),
    ...(meta.ocr && settings.ocr
      ? [
          {
            label: "ocr_rec.onnx",
            group: "extras" as const,
            total: meta.bytes["ocr_rec.onnx"] ?? 0,
          },
        ]
      : []),
  ]);

  const columnJobs = columns.map((column) =>
    limited(async () => {
      const task = report(columnLabel(column.file), { group: "index" });
      try {
        const bytes = await fetchProgress(assetUrl(`${column.file}`), task);
        column.apply(bytes);
        task.done(mb(bytes.byteLength));
      } catch (error) {
        invalidateCompleteTransfer(assetUrl(column.file));
        task.fail((error as Error).message);
        throw error;
      }
    }),
  );

  const catalogueJob = limited(async () => {
    const task = report("catalogue", { group: "index" });
    try {
      const bytes = await fetchProgress(assetUrl(`catalogue.json`), task);
      gallery.catalogue = JSON.parse(new TextDecoder().decode(bytes));
      transfers.delete(assetUrl("catalogue.json"));
      task.done(`${nf.format(meta.printings)} printings`);
    } catch (error) {
      invalidateCompleteTransfer(assetUrl("catalogue.json"));
      task.fail((error as Error).message);
      throw error;
    }
  });

  // A session starts building the moment its own bytes land, rather than after every
  // other download has finished. The detector's shader build is several seconds on its
  // own — overlapping it with the embedder download is most of the win.
  // All three graphs are the same weights at different fixed rasters. 320 is the mobile
  // default: across the two real camera clips it kept 240/240 detections over the policy
  // floor while reducing hardware WebGL inference by 31.5% versus 416. The setting keeps
  // the larger exports available for diagnosis.
  const detectorJob = limited(async () => {
    const task = report(detectorFile, { group: "detector" });
    try {
      const detectorProviders = /[?&]detwasm\b/.test(location.search) ? ["wasm"] : providers;
      const cacheKey = `${assetUrl(detectorFile)}:${detectorProviders[0]}`;
      const retained = detectors.get(cacheKey);
      if (retained) {
        task.done(`${retained.width}px, ${detectorProviders[0]}`);
        return retained;
      }
      const bytes = await fetchProgress(assetUrl(`${detectorFile}`), task);
      const detectorRuntime = await loadRuntime(detectorProviders[0]);
      const built = clock();
      // `?detwasm` runs detection on the CPU. Worth having as a switch rather than a
      // guess: the model's cost on Safari's WebGPU barely changed between a 640 and a 416
      // input, which says it is per-dispatch overhead rather than arithmetic — and a
      // hundred small dispatches is exactly the shape WASM can win.
      const built_detector = await buildSerial(() => {
        // Fixed input/output shapes make this pool reach a stable high-water mark during
        // warm-up. Reusing it avoids dozens of create/deleteTexture calls on every frame.
        if (detectorProviders[0] === "webgl") detectorRuntime.env.webgl.textureCacheMode = "full";
        return PoseDetector.create(
          detectorRuntime,
          bytes,
          detectorProviders,
          Number(settings.detector),
        );
      });
      detectors.set(cacheKey, built_detector);
      transfers.delete(assetUrl(detectorFile));
      log(
        "model",
        `detector session built in ${ms(built())} — ` +
          `${built_detector.width}x${built_detector.height} input, ` +
          `${
            detectorProviders[0] === "webgpu"
              ? "WebGPU persistent IO"
              : detectorProviders[0] === "webgl"
                ? "WebGL2 pooled IO"
                : "WASM"
          }`,
      );
      task.done(`${built_detector.width}px, ${detectorProviders[0]}`);
      return built_detector;
    } catch (error) {
      invalidateCompleteTransfer(assetUrl(detectorFile));
      task.fail((error as Error).message);
      throw new Error(`The card detector could not load: ${(error as Error).message}`);
    }
  });

  const backend = providers[0];
  const embedderJob = limited(async () => {
    const task = report(embedderFile, { group: "recogniser" });
    try {
      const cacheKey = `${assetUrl(embedderFile)}:${backend}`;
      const retained = embedders.get(cacheKey);
      if (retained) {
        task.done(backend);
        return retained;
      }
      const bytes = await fetchProgress(assetUrl(embedderFile), task);
      const built = clock();
      const session = await buildSerial(() => {
        // The FP32 WebGL embedder is large but fixed-shape. Its full pool consumes no more
        // than the first run's unavoidable peak and then stays flat; transient mode was
        // 2.7x slower and repeatedly churned roughly 169 MiB through the WebKit driver.
        if (backend === "webgl") ort.env.webgl.textureCacheMode = "full";
        return Embedder.create(ort, bytes, backend, gallery.embedDim);
      });
      embedders.set(cacheKey, session);
      transfers.delete(assetUrl(embedderFile));
      log(
        "model",
        `embedder session built in ${ms(built())} on ${backend} — ` +
          `in [${session.inputName}] out [${session.outputName}], ` +
          `${gallery.embedDim}-d over ${nf.format(gallery.groups)} rows`,
      );
      task.done(backend);
      return session;
    } catch (error) {
      invalidateCompleteTransfer(assetUrl(embedderFile));
      task.fail((error as Error).message);
      throw new Error(`The embedder could not load: ${(error as Error).message}`);
    }
  });

  // The fitted logistic that turns evidence into P(correct). Its absence is not fatal but
  // is worth saying out loud: the fallback is a hand-weighted blend at a different, and
  // less well-grounded, operating point.
  const calibrationJob = () =>
    meta.calibration
      ? limited(async () => {
          const task = report("decision policy");
          try {
            const bytes = await fetchProgress(assetUrl("calibration.json"), task);
            const fitted = JSON.parse(new TextDecoder().decode(bytes)) as {
              version: number;
              features: string[];
              weights: number[];
              bias: number;
            };
            // Version 2 dropped the printed identifiers from the feature vector; a
            // version-1 file carries weights fitted against them (and inverted), so it
            // is refused and the page falls back to the hand-weighted fusion.
            if (fitted.version !== 2) throw new Error(`unknown version ${fitted.version}`);
            // The weights are positional. A file whose features were reordered without a
            // version bump would be applied to the wrong evidence, silently and with
            // entirely plausible-looking scores — so check the list, not just the version.
            const namesMatch =
              Array.isArray(fitted.features) &&
              fitted.features.length === FEATURE_NAMES.length &&
              fitted.features.every((name, i) => name === FEATURE_NAMES[i]);
            if (!namesMatch) throw new Error("feature vector does not match this build");
            if (fitted.weights?.length !== FEATURE_NAMES.length) throw new Error("weight count");
            gallery.calibration = { weights: fitted.weights, bias: fitted.bias };
            task.done("calibrated");
          } catch (error) {
            task.fail((error as Error).message);
            warnings.push(`Uncalibrated decisions (${(error as Error).message}).`);
          } finally {
            transfers.delete(assetUrl("calibration.json"));
          }
        })
      : Promise.resolve();

  // Observe every rejection immediately and finish in-flight required work before retry.
  // Successful downloads and compiled sessions remain available to the next attempt.
  const results = await Promise.allSettled([...columnJobs, catalogueJob, detectorJob, embedderJob]);
  const failed = results.find((result) => result.status === "rejected");
  if (failed?.status === "rejected") throw failed.reason;
  const detector = await detectorJob;
  const embedder = await embedderJob;
  if (backend !== "webgl" || /[?&]detwasm\b/.test(location.search)) {
    log(
      "model",
      `WASM threads after runtime initialization: requested=${wasmThreads.requested ?? "default"} ` +
        `configured=${wasmThreads.configured} applied=${ort.env.wasm.numThreads}`,
    );
  }

  const ready: Loaded = {
    meta,
    embedder,
    detector,
    backend,
    spans: null,
    orb: false,
    ocr: null,
    warnings,
  };

  // The collector-number reader arrives after boot, not during it: 21 MB that no scan
  // strictly needs, and the camera should not wait on it. Until it lands, ambiguous
  // reads simply stay symbol-only; `ready.ocr` flips when it is up.
  // Kept so a stage switched on later can load what it needs without a reload.
  lastReport = report;
  transfers.clear();
  invalidTransfers.clear();
  // Every optional stage is independent of readiness. Its own reporter records failures,
  // while recognition starts with the evidence currently available and improves as it lands.
  void calibrationJob();
  for (const stage of ["orb", "ocr", "setSymbols"] as const) {
    if (settings[stage]) void ensureStage(ready, stage);
  }

  group("boot", `ready in ${ms(bootClock())} — ${backend}, ORB ${ready.orb ? "on" : "off"}`);
  log("boot", "retrieval channels", {
    legacyHashes: !!gallery.hashes,
    richSignature: gallery.hasSignature,
    embeddings: gallery.hasEmbeddings ? `${gallery.embedDim}-d` : false,
    setSymbols: gallery.setSymbols?.size ?? 0,
    calibration: !!gallery.calibration,
    orbVerification: ready.orb,
    ocr: !meta.ocr ? false : settings.ocr ? "loading in background" : "off in settings",
  });
  const off = (["orb", "ocr", "setSymbols"] as const).filter((key) => !settings[key]);
  if (off.length) log("boot", `switched off in settings: ${off.join(", ")}`);
  if (warnings.length) log("boot", "degraded:", warnings);
  groupEnd();
  return ready;
}

export { ort };
